import { FaArrowLeft, FaArrowRight } from "react-icons/fa";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ManageBranchService } from "../api/services";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const UserCardTransactionHistory = () => {
  const navigate = useNavigate();

  // --------------------------------------
  // STATE DECLARATIONS
  // --------------------------------------
  const thStyle = {
    padding: "12px",
    fontWeight: "600"
  };
  const tdStyle = {
  padding: "12px",
  textAlign: "center",
  border: "1px solid #e5e7eb",
  color: "#111827",
  fontSize: "14px",
};
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showPickerModal, setShowPickerModal] = useState(false);

  const [superAdmin_id, setSuperAdminId] = useState("");

  const [monthlyTransactions, setMonthlyTransactions] = useState([]); // raw monthly API
  const [dailyTransactions, setDailyTransactions] = useState([]); // daily filtered

  const [activeTab, setActiveTab] = useState("monthly");

  // Daily summary stats
  const [dailySummary, setDailySummary] = useState({
    recharges: 0,
    swaps: 0,
    totalTransactions: 0,
    rechargeCards: 0,
    swapCards: 0,
    uniqueCards: 0,
    rechargeAmount: 0,
    swapAmount: 0,
    totalAmount: 0
  });
  const [isLoading, setIsLoading] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 10;

  // Summary stats
  const [monthlySummary, setMonthlySummary] = useState({
    recharges: 0,
    swaps: 0,
    totalTransactions: 0,
    rechargeCards: 0,
    swapCards: 0,
    uniqueCards: 0,
    rechargeAmount: 0,
    swapAmount: 0,
    totalAmount: 0
  });

  // --------------------------------------
  // HELPERS
  // --------------------------------------
  const getMonthLabel = (date) =>
    date.toLocaleString("default", { month: "long", year: "numeric" });

  const formatMonthYear = (date) => {
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const y = date.getFullYear();
    return `${m}-${y}`;
  };

  const formatDate = (date) => {
    const d = String(date.getDate()).padStart(2, "0");
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const y = date.getFullYear();
    return `${y}-${m}-${d}`;
  };

  function convertToYMD(dateString) {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  // Calculate summary from transactions
  const calculateSummary = (transactions) => {
    const rechargeTransactions = transactions.filter(tx => tx.transaction_type === "AGENT");
    const swapTransactions = transactions.filter(tx => tx.transaction_type === "SWAP");
    
    const recharges = rechargeTransactions.length;
    const swaps = swapTransactions.length;
    
    const rechargeCards = new Set(rechargeTransactions.map(tx => tx.card_id)).size;
    const swapCards = new Set(swapTransactions.map(tx => tx.card_id)).size;
    const uniqueCards = new Set(transactions.map(tx => tx.card_id)).size;
    
    const rechargeAmount = rechargeTransactions.reduce((sum, tx) => sum + Number(tx.amount || 0), 0);
    const swapAmount = swapTransactions.reduce((sum, tx) => sum + Number(tx.amount || 0), 0);
    const totalAmount = transactions.reduce((sum, tx) => sum + Number(tx.amount || 0), 0);

    return {
      recharges,
      swaps,
      totalTransactions: transactions.length,
      rechargeCards,
      swapCards,
      uniqueCards,
      rechargeAmount,
      swapAmount,
      totalAmount
    };
  };

  // --------------------------------------
  // FETCH MONTHLY API
  // --------------------------------------
  const fetchTransactionsForMonth = (userId, date) => {
    const payload = {
      user_id: userId,
      month: formatMonthYear(date),
    };

    setIsLoading(true);

    ManageBranchService.GetCardTransactionHistory(payload)
      .then((res) => {
        const monthly = res.data.results || [];
        setMonthlyTransactions(monthly);
        
        // Calculate summary
        const summary = calculateSummary(monthly);
        setMonthlySummary(summary);
        
        setIsLoading(false);
      })
      .catch((err) => {
        console.error("Fetch failed:", err);
        setMonthlyTransactions([]);
        setMonthlySummary({
          recharges: 0,
          swaps: 0,
          totalTransactions: 0,
          rechargeCards: 0,
          swapCards: 0,
          uniqueCards: 0,
          rechargeAmount: 0,
          swapAmount: 0,
          totalAmount: 0
        });
        setIsLoading(false);
      });
  };

  // --------------------------------------
  // FILTER DAILY
  // --------------------------------------
  const filterDailyTransactions = (userId, date) => {
    const payload = {
      user_id: userId,
      date: convertToYMD(date),
    };

    setIsLoading(true);

    ManageBranchService.GetCardTransactionHistory(payload)
      .then((res) => {
        const daily = res.data.results || [];
        setDailyTransactions(daily);
        
        // Calculate daily summary
        const summary = calculateSummary(daily);
        setDailySummary(summary);
        
        setIsLoading(false);
      })
      .catch((err) => {
        console.error("Fetch failed:", err);
        setDailyTransactions([]);
        setDailySummary({
          recharges: 0,
          swaps: 0,
          totalTransactions: 0,
          rechargeCards: 0,
          swapCards: 0,
          uniqueCards: 0,
          rechargeAmount: 0,
          swapAmount: 0,
          totalAmount: 0
        });
        setIsLoading(false);
      });
  };

  // --------------------------------------
  // CHANGE DAY (Prev / Next)
  // --------------------------------------
  const changeDay = (direction) => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + direction);
    setSelectedDate(newDate);
    filterDailyTransactions(superAdmin_id, newDate);
  };

  // --------------------------------------
  // CHANGE MONTH
  // --------------------------------------
  const handleMonthChange = (direction) => {
    const newDate = new Date(selectedMonth);
    newDate.setMonth(newDate.getMonth() + direction);
    setSelectedMonth(newDate);
    fetchTransactionsForMonth(superAdmin_id, newDate);
  };

  // --------------------------------------
  // ON LOAD
  // --------------------------------------
  useEffect(() => {
    const token = localStorage.getItem("jwtToken");
    if (!token) navigate("/");

    const userId = localStorage.getItem("admin_id");
    setSuperAdminId(userId);

    fetchTransactionsForMonth(userId, selectedMonth);
  }, []);

  // --------------------------------------
  // PAGINATION
  // --------------------------------------
  const lastIndex = currentPage * recordsPerPage;
  const firstIndex = lastIndex - recordsPerPage;
  const paginatedDaily = dailyTransactions.slice(firstIndex, lastIndex);
  const totalPages = Math.ceil(dailyTransactions.length / recordsPerPage);

  // --------------------------------------
  // MONTH PICKER MODAL
  // --------------------------------------
  const renderDatepickerModal = () =>
    showPickerModal && (
      <div className="modal d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content rounded">
            <div className="modal-header">
              <h5 className="modal-title fw-bold">Select Month</h5>
              <button className="btn-close" onClick={() => setShowPickerModal(false)} />
            </div>

            <div className="modal-body text-center">
              <DatePicker
                selected={selectedMonth}
                onChange={(date) => setSelectedMonth(date)}
                showMonthYearPicker
                dateFormat="MM/yyyy"
                inline
              />
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowPickerModal(false)}>
                Cancel
              </button>

              <button
                className="btn btn-primary"
                onClick={() => {
                  fetchTransactionsForMonth(superAdmin_id, selectedMonth);
                  setShowPickerModal(false);
                }}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      </div>
    );

  // --------------------------------------
  // UI
  // --------------------------------------
  return (
    <div className="container mt-4">
      {/* Loader */}
      {isLoading && (
        <div className="loader">
          <div className="lds-spinner">
            {Array(12)
              .fill()
              .map((_, i) => (
                <div key={i}></div>
              ))}
          </div>
        </div>
      )}

      <button className="btn btn-outline-secondary mb-3" onClick={() => navigate(-1)}>
        ← Back
      </button>

      {/* Tabs */}
      <ul className="nav nav-tabs mb-3">
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === "monthly" ? "active" : ""}`}
            onClick={() => setActiveTab("monthly")}
          >
            Monthly Summary
          </button>
        </li>

        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === "daily" ? "active" : ""}`}
            onClick={() => setActiveTab("daily")}
          >
            Daily Summary
          </button>
        </li>
      </ul>

      {/* MONTHLY TAB */}
      {activeTab === "monthly" && (
        <>
          <div
      style={{
        background: "linear-gradient(to bottom right, #ff7e5f, #feb47b)",
        padding: "18px 20px",
        borderRadius: "16px",
        marginBottom: "20px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "10px",
        color: "white",
        boxShadow: "0 4px 6px rgba(0,0,0,0.12)",
      }}
    >
      {/* TITLE */}
      <h5
        style={{
          margin: 0,
          fontWeight: "600",
          fontSize: "16px",
        }}
      >
        Monthly Summary
      </h5>

      {/* MONTH CONTROLS */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "14px",
        }}
      >
        <button
          className="btn btn-outline-light btn-sm rounded-circle border-0"
          onClick={() => handleMonthChange(-1)}
          aria-label="Previous month"
        >
          <FaArrowLeft />
        </button>

        <span
          onClick={() => setShowPickerModal(true)}
          style={{
            fontWeight: "700",
            fontSize: "15px",
            cursor: "pointer",
            padding: "6px 16px",
            borderRadius: "20px",
            background: "rgba(255,255,255,0.2)",
          }}
        >
          {getMonthLabel(selectedMonth)}
        </span>

        <button
          className="btn btn-outline-light btn-sm rounded-circle border-0"
          onClick={() => handleMonthChange(1)}
          aria-label="Next month"
        >
          <FaArrowRight />
        </button>
      </div>
    </div>


          {/* Summary Table */}
          <div className="my-3">
            <div className="table-responsive shadow-sm border rounded bg-white">
              <table className="table table-bordered text-center mb-0">
                <thead className="table-light">
                  <tr>
                    <th></th>
                    <th>Transactions</th>
                    <th>Cards</th>
                    <th>Units</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="fw-bold text-start">Recharges</td>
                    <td>{monthlySummary.recharges}</td>
                    <td>{monthlySummary.rechargeCards}</td>
                    <td className="text-danger fw-bold">{monthlySummary.rechargeAmount} ↓</td>
                  </tr>
                  <tr>
                    <td className="fw-bold text-start">Swap</td>
                    <td>{monthlySummary.swaps}</td>
                    <td>{monthlySummary.swapCards}</td>
                    <td>{monthlySummary.swapAmount}</td>
                  </tr>
                  <tr className="table-light">
                    <td className="fw-bold text-start">Total</td>
                    <td className="fw-bold">{monthlySummary.totalTransactions}</td>
                    <td className="fw-bold">{monthlySummary.uniqueCards}</td>
                    <td className="fw-bold text-danger">{monthlySummary.totalAmount} ↓</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Monthly Table */}
          <div  style={{
              marginTop: "30px",
              backgroundColor: "white",
              padding: "20px",
              borderRadius: "16px",
              boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
            }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead >
                <tr style={{
                      background: "linear-gradient(to bottom right, #ff7e5f, #feb47b)",
                      textAlign: "center",
                      color: "white"
                    }}>
                  <th style={thStyle}>Date</th>
                  <th style={thStyle}>Card Name</th>
                  <th style={thStyle}>By</th>
                  <th style={thStyle}>To</th>
                  <th style={thStyle}>Amount</th>
                </tr>
              </thead>

              <tbody>
                {monthlyTransactions.length > 0 ? (
                  monthlyTransactions.map((txn) => (
                    <tr key={txn.transaction_id}>
                      <td  style={tdStyle}>{txn.transaction_time}</td>
                      <td  style={tdStyle}>{txn.card_name}</td>
                      <td  style={tdStyle}>{txn.by_name}</td>
                      <td  style={tdStyle}>{txn.to_name}</td>
                      <td  style={tdStyle}>{txn.amount}</td>
                    </tr>
                  ))
                ) : (
                  <tr className="text-center">
                    <td colSpan="5">No transactions found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* DAILY TAB */}
      {activeTab === "daily" && (
        <>
      <div
      style={{
        background: "linear-gradient(to bottom right, #ff7e5f, #feb47b)",
        padding: "18px 20px",
        borderRadius: "16px",
        marginBottom: "20px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "10px",
        color: "white",
        boxShadow: "0 4px 6px rgba(0,0,0,0.12)",
      }}
    >
      {/* TITLE */}
      <h5
        style={{
          margin: 0,
          fontWeight: "600",
          fontSize: "16px",
        }}
      >
        Daily Summary
      </h5>

      {/* DATE CONTROLS */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "14px",
        }}
      >
        <button
          className="btn btn-outline-light btn-sm rounded-circle border-0"
          onClick={() => changeDay(-1)}
          aria-label="Previous day"
        >
          <FaArrowLeft />
        </button>

        <div
          style={{
            background: "rgba(255,255,255,0.2)",
            borderRadius: "20px",
            padding: "2px 8px",
          }}
        >
          <DatePicker
            selected={selectedDate}
            onChange={(date) => {
              setSelectedDate(date);
              filterDailyTransactions(superAdmin_id, date);
            }}
            dateFormat="dd/MM/yyyy"
            className="form-control form-control-sm border-0 text-center fw-bold"
            style={{
               background: "rgba(255,255,255,0.2)"
             
            }}
          />
        </div>

        <button
          className="btn btn-outline-light btn-sm rounded-circle border-0"
          onClick={() => changeDay(1)}
          aria-label="Next day"
        >
          <FaArrowRight />
        </button>
      </div>
    </div>

          {/* Daily Summary Table */}
          <div className="my-3">
            <div className="table-responsive shadow-sm border rounded bg-white">
              <table className="table table-bordered text-center mb-0">
                <thead className="table-light">
                  <tr >
                    <th></th>
                    <th>Transactions</th>
                    <th>Cards</th>
                    <th>Units</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="fw-bold text-start">Recharges</td>
                    <td>{dailySummary.recharges}</td>
                    <td>{dailySummary.rechargeCards}</td>
                    <td className="text-danger fw-bold">{dailySummary.rechargeAmount} ↓</td>
                  </tr>
                  <tr>
                    <td className="fw-bold text-start">Swap</td>
                    <td>{dailySummary.swaps}</td>
                    <td>{dailySummary.swapCards}</td>
                    <td>{dailySummary.swapAmount}</td>
                  </tr>
                  <tr className="table-light">
                    <td className="fw-bold text-start">Total</td>
                    <td className="fw-bold">{dailySummary.totalTransactions}</td>
                    <td className="fw-bold">{dailySummary.uniqueCards}</td>
                    <td className="fw-bold text-danger">{dailySummary.totalAmount} ↓</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Daily Table */}
          <div  style={{
              marginTop: "30px",
              backgroundColor: "white",
              padding: "20px",
              borderRadius: "16px",
              boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
            }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead className="table-primary">
                <tr style={{
                      background: "linear-gradient(to bottom right, #ff7e5f, #feb47b)",
                      textAlign: "center",
                      color: "white"
                    }}>
                  <th style={thStyle}>Time</th>
                  <th style={thStyle}>Card Name</th>
                  <th style={thStyle}>By</th>
                  <th style={thStyle}>To</th>
                  <th style={thStyle}>Amount</th>
                </tr>
              </thead>

              <tbody>
                {paginatedDaily.length > 0 ? (
                  paginatedDaily.map((txn) => (
                    <tr key={txn.transaction_id}>
                      <td style={tdStyle}>{txn.transaction_time}</td>
                      <td style={tdStyle}>{txn.card_name}</td>
                      <td style={tdStyle}>{txn.by_name}</td>
                      <td style={tdStyle}>{txn.to_name}</td>
                      <td style={tdStyle}>{txn.amount}</td>
                    </tr>
                  ))
                ) : (
                  <tr className="text-center">
                    <td colSpan="5">No transactions found for this date.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {dailyTransactions.length > 10 && (
            <div className="d-flex justify-content-center mt-3">
              <button
                className="btn btn-outline-primary mx-2"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
              >
                Prev
              </button>

              <span className="fw-bold">
                {currentPage} / {totalPages}
              </span>

              <button
                className="btn btn-outline-primary mx-2"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(currentPage + 1)}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {renderDatepickerModal()}
    </div>
  );
};

export default UserCardTransactionHistory;